import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import {
  CallStatusInHourModel,
  CallStatusModel,
  DateRange,
  FeedbackTypeRankingModel,
  OperatorActivityModel,
  OperatorKpiModel,
  OperatorOrderStatisticModel,
  OperatorRankingModel,
  toHttpParams
} from 'hx-services';

/**
 * Filter for /reports/calls/funnel. `date` is the visible period; `prev` is the previous
 * window of the same length (computed on the front, sent as prev_from/prev_to) for Δ.
 * `timeZone` is the IANA tz used for day/hour bucketing — sent as the `tz` HTTP param.
 */
export interface CallFunnelFilter {
  date?: DateRange;
  prev?: DateRange;
  countryId?: number;
  cityId?: number;
  timeZone?: string;
}

/** One funnel snapshot: counts by the stage where an inbound call ended. */
export interface CallFunnelStagesModel {
  answered: number;
  operatorNoAnswer: number;
  abandonedInQueue: number;
  droppedOnGreeting: number;
}

export interface CallFunnelDayModel extends CallFunnelStagesModel {
  day: string;
}

export interface CallFunnelHourModel extends CallFunnelStagesModel {
  hour: number;
}

export interface CallFunnelReportModel {
  current: CallFunnelStagesModel;
  previous: CallFunnelStagesModel | null;
  byDay: CallFunnelDayModel[];
  byHour: CallFunnelHourModel[];
}

@Injectable({
  providedIn: 'root'
})
export class ReportService {

  constructor(
    private http: HttpClient,
  ) {
  }

  getCallReport(params: { date?: DateRange }): Observable<OperatorKpiModel[]> {
    return this.http.get<OperatorKpiModel[]>(`/api/vn/reports/calls/operator-kpi`, {params: toHttpParams(params, true)});
  }

  /** «Сводка по операторам»: звонки и заказы по каждому оператору за период. */
  getOperatorSummary(params: { date?: DateRange, timeZone?: string }): Observable<OperatorSummaryModel[]> {
    return this.http.get<OperatorSummaryModel[]>(`/api/vn/reports/calls/operator-summary`, {params: toHttpParams(params, true)});
  }

  /** Карточка оператора в «Сводке»: разбивка по дням, распределение оценок, последние отзывы. */
  getOperatorSummaryDetail(operatorId: number, params: { date?: DateRange, timeZone?: string }): Observable<OperatorSummaryDetailModel> {
    return this.http.get<OperatorSummaryDetailModel>(`/api/vn/reports/calls/operator-summary/${operatorId}`, {params: toHttpParams(params, true)});
  }

  getCallStatusReport(params: { date?: DateRange }): Observable<CallStatusModel[]> {
    return this.http.get<CallStatusModel[]>(`/api/vn/reports/calls/status-statistics`, {params: toHttpParams(params, true)});
  }

  getCallInHour(params: {date: string, interval: number }): Observable<CallStatusInHourModel[]> {
    return this.http.get<CallStatusInHourModel[]>(`/api/vn/reports/calls/status-statistics/in-hour`, { params: toHttpParams(params, true) });
  }

  getOperatorActivity(date: string): Observable<OperatorActivityModel[]> {
    return this.http.get<OperatorActivityModel[]>(`/api/vn/reports/operator-activity?date=${date}`);
  }

  getOperatorActivityDatailed(date: string): Observable<OperatorActivityDetailedReportModel[]> {
    return this.http.get<OperatorActivityDetailedReportModel[]>(`/api/vn/reports/operator-activity-detailed?date=${date}`);
  }

  getOperatorOrderStat(params: { date?: DateRange }): Promise<OperatorOrderStatisticModel[]> {
    return firstValueFrom(this.http.get<OperatorOrderStatisticModel[]>(`/api/vn/reports/operator-order-statistics`, {params: toHttpParams(params, true)}));
  }

  getOperatorRating(params: { date?: DateRange }): Observable<OperatorRankingModel[]> {
    return this.http.get<OperatorRankingModel[]>(`/api/vn/feedbacks/operator/rating`, {params: toHttpParams(params, true)});
  }

  getFeedbackTypeRating(params: { date?: DateRange }): Observable<FeedbackTypeRankingModel[]> {
    return this.http.get<FeedbackTypeRankingModel[]>(`/api/vn/feedbacks/store/type/rating`, {params: toHttpParams(params, true)});
  }

  getCallFunnelReport(filter: CallFunnelFilter): Observable<CallFunnelReportModel> {
    return this.http.get<CallFunnelReportModel>(`/api/vn/reports/calls/funnel`, {params: this.callFunnelParams(filter)});
  }

  /** camelCase filter → snake_case HTTP query params (incl. prev window + `tz`), same scheme as monitoring.service.ts. */
  private callFunnelParams(filter: CallFunnelFilter): HttpParams {
    let params = new HttpParams();
    if (filter.date?.from) {
      params = params.set('date_from', filter.date.from);
    }
    if (filter.date?.to) {
      params = params.set('date_to', filter.date.to);
    }
    if (filter.prev?.from) {
      params = params.set('prev_from', filter.prev.from);
    }
    if (filter.prev?.to) {
      params = params.set('prev_to', filter.prev.to);
    }
    if (filter.countryId !== undefined && filter.countryId !== null) {
      params = params.set('country_id', String(filter.countryId));
    }
    if (filter.cityId !== undefined && filter.cityId !== null) {
      params = params.set('city_id', String(filter.cityId));
    }
    if (filter.timeZone) {
      params = params.set('tz', filter.timeZone);
    }
    return params;
  }
}

export interface OperatorActivityDetailedReportModel {
  lastname: string;
  firstname: string;
  patronymic: string;
  asteriskId: string;
  onlineTime: number;
  offlineTime: number;
  isOnline: boolean;
  lastOnlineTime: number;
}

/** Строка ответа /reports/calls/operator-summary (OperatorSummaryModel на бэкенде). */
export interface OperatorSummaryModel {
  /** null — принятые звонки, у которых оператор не определён. */
  operatorId?: number | null;
  operatorName?: string | null;
  answeredCalls: number;
  /** Дозвоны на оператора, которые он не взял (как «% недозвона» в «Мониторинге»). */
  missedCalls: number;
  /** Все дозвоны на оператора — знаменатель доли пропущенных. */
  dialCount: number;
  talkSeconds: number;
  createdCount: number;
  modifiedCount: number;
  cancelledCount: number;
  /** Созданные оператором заказы, выданные клиенту в этом же периоде. */
  receivedCount: number;
  /** Средняя оценка клиентов (1–5); null — оценок за период нет. */
  rating?: number | null;
  ratingCount: number;
  /** Сумма созданных заказов без отменённых и возвращённых, по валютам. */
  revenue: { currency: string | null; amount: number }[];
}

/** Ответ /reports/calls/operator-summary/{operatorId} (OperatorSummaryDetailModel на бэкенде). */
export interface OperatorSummaryDetailModel {
  /** Пусто, если период длиннее трёх месяцев. */
  days: { date: string; answeredCalls: number; talkSeconds: number; createdCount: number }[];
  /** Пять корзин, от 5 до 1. */
  ratings: { rating: number; count: number }[];
  feedbacks: {
    id: number;
    date: string;
    rating?: number | null;
    text?: string | null;
    orderId?: number | null;
    orderNumber?: string | null;
  }[];
}
